import React, { useState } from 'react';
import { Radio, Music, Calendar, Mail, Phone, MapPin, Send, Heart, Facebook, Instagram, Twitter, Play, Volume2, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Home2 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '' });
  const { toast } = useToast();

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Feedback Received!",
      description: "Thank you for your feedback.",
    });
    setFeedbackForm({ name: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Sticky Sidebar Navigation */}
      <nav className="fixed left-0 top-0 h-full w-20 bg-gradient-to-b from-red-900 via-black to-red-900 border-r border-red-500/20 z-50 hidden lg:flex flex-col items-center py-8 gap-8">
        <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mb-4">
          <Radio className="w-6 h-6 text-white" />
        </div>
        <a href="#home" className="w-12 h-12 hover:bg-red-500/20 rounded-lg flex items-center justify-center transition-all group">
          <div className="w-2 h-2 bg-red-500 rounded-full group-hover:scale-150 transition-all"></div>
        </a>
        <a href="#about" className="w-12 h-12 hover:bg-red-500/20 rounded-lg flex items-center justify-center transition-all group">
          <div className="w-2 h-2 bg-white/40 rounded-full group-hover:scale-150 group-hover:bg-red-500 transition-all"></div>
        </a>
        <a href="#shows" className="w-12 h-12 hover:bg-red-500/20 rounded-lg flex items-center justify-center transition-all group">
          <div className="w-2 h-2 bg-white/40 rounded-full group-hover:scale-150 group-hover:bg-red-500 transition-all"></div>
        </a>
        <a href="#events" className="w-12 h-12 hover:bg-red-500/20 rounded-lg flex items-center justify-center transition-all group">
          <div className="w-2 h-2 bg-white/40 rounded-full group-hover:scale-150 group-hover:bg-red-500 transition-all"></div>
        </a>
        <a href="#contact" className="w-12 h-12 hover:bg-red-500/20 rounded-lg flex items-center justify-center transition-all group">
          <div className="w-2 h-2 bg-white/40 rounded-full group-hover:scale-150 group-hover:bg-red-500 transition-all"></div>
        </a>
      </nav>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-lg border-b border-red-500/20">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <Radio className="w-6 h-6 text-red-500" />
            <span className="font-bold text-white">WVOD 93</span>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="bg-black border-t border-red-500/20 p-4 space-y-2">
            <a href="#home" className="block py-2 text-white hover:text-red-500" onClick={() => setMobileMenuOpen(false)}>Home</a>
            <a href="#about" className="block py-2 text-white hover:text-red-500" onClick={() => setMobileMenuOpen(false)}>About</a>
            <a href="#shows" className="block py-2 text-white hover:text-red-500" onClick={() => setMobileMenuOpen(false)}>Shows</a>
            <a href="#events" className="block py-2 text-white hover:text-red-500" onClick={() => setMobileMenuOpen(false)}>Events</a>
            <a href="#contact" className="block py-2 text-white hover:text-red-500" onClick={() => setMobileMenuOpen(false)}>Contact</a>
          </div>
        )}
      </header>

      {/* Main Content with Left Margin */}
      <div className="lg:ml-20">
        {/* Split Screen Hero */}
        <section id="home" className="min-h-screen flex flex-col lg:flex-row">
          {/* Left Side - Content */}
          <div className="lg:w-1/2 bg-gradient-to-br from-red-900 via-black to-black p-8 lg:p-16 flex items-center justify-center">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full mb-6">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-bold uppercase tracking-widest text-red-500">Broadcasting Live</span>
              </div>
              
              <h1 className="text-6xl lg:text-8xl font-black mb-6 leading-none">
                WVOD<br />
                <span className="text-red-500">POWER</span><br />
                93
              </h1>
              
              <p className="text-2xl font-bold text-white/80 mb-4 uppercase tracking-wider">The Voice of the Diaspora</p>
              
              <p className="text-lg text-white/60 mb-8 leading-relaxed">
                Music the way it used to be. Classic hits, soulful melodies, and timeless tunes that transcend borders and generations.
              </p>

              {/* Live Player Embed */}
              <div className="bg-black/50 backdrop-blur-sm rounded-xl overflow-hidden border border-red-500/20 mb-6">
                <iframe 
                  src="https://s4.radio.co/s5f6084ed4/listen" 
                  frameBorder="0" 
                  scrolling="no" 
                  height="200" 
                  width="100%"
                  title="WVOD Power 93 Live Stream"
                  className="w-full"
                ></iframe>
              </div>

              <div className="flex gap-4">
                <button className="px-8 py-4 bg-red-500 hover:bg-red-600 rounded-full font-bold uppercase tracking-wider transition-all transform hover:scale-105">
                  Listen Now
                </button>
                <button className="px-8 py-4 bg-white/10 hover:bg-white/20 rounded-full font-bold uppercase tracking-wider transition-all">
                  View Schedule
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Video/Image */}
          <div className="lg:w-1/2 relative bg-black">
            <video 
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              poster=""
            >
              <source src="https://video.wixstatic.com/video/4bfe2c_db0d778e179a423eb2a97bb732047c6d/1080p/mp4/file.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/50"></div>
          </div>
        </section>

        {/* Magazine Grid Layout - About */}
        <section id="about" className="py-24 px-4 lg:px-16 bg-black">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-8">
              {/* Large Logo Card */}
              <div className="lg:col-span-5 bg-gradient-to-br from-red-500/10 to-black border border-red-500/20 rounded-2xl p-8 flex items-center justify-center">
                <img 
                  src="https://static.wixstatic.com/media/4bfe2c_5a792627a08d495cb304086fc0206377~mv2.png" 
                  alt="WVOD Power 93 Logo" 
                  className="w-full max-w-md"
                />
              </div>

              {/* Text Content */}
              <div className="lg:col-span-7 flex flex-col justify-center">
                <h2 className="text-5xl lg:text-6xl font-black mb-6 leading-tight">
                  MUSIC POWERED BY<br />
                  <span className="text-red-500">INFAMOUS SOLJAH</span>
                </h2>
                
                <div className="space-y-4 text-lg text-white/70">
                  <p>
                    Join <span className="text-red-500 font-bold">DJ MG</span>, <span className="text-red-500 font-bold">C Fresh</span>, <span className="text-red-500 font-bold">Infamous SoLJah sound</span> and the rest of the WVOD POWER 93 team as we take you on a musical journey through the ages.
                  </p>
                  <p>
                    From the golden era to modern hits, we curate the perfect blend of tracks that celebrate the rich tapestry of music while providing top of the hour news, informative shows on the history of the diaspora, entertainment and daily devotions.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-4">
                    {['Hip Hop', 'House', 'RnB', 'Reggae', 'Dancehall', 'Calypso', 'Soca', 'Reggaeton', 'Afrobeats'].map((genre, i) => (
                      <span key={i} className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-sm font-bold text-red-500 uppercase tracking-wider">
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bold Typography Shows Section */}
        <section id="shows" className="py-24 px-4 lg:px-16 bg-gradient-to-b from-black via-red-900/10 to-black">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-7xl lg:text-9xl font-black mb-16 leading-none">
              WEEKLY<br />
              <span className="text-red-500">SHOWS</span>
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Morning Drive', time: 'Mon-Fri | 6AM-10AM', desc: 'Start your day with classic hits and daily devotions' },
                { name: 'Midday Classics', time: 'Mon-Fri | 12PM-2PM', desc: 'Perfect lunch companion with timeless tracks' },
                { name: 'Diaspora History', time: 'Tue & Thu | 7PM-8PM', desc: 'Exploring the rich history of the diaspora' },
                { name: 'Weekend Vibes', time: 'Sat-Sun | 2PM-6PM', desc: 'Non-stop hits across all genres' },
                { name: 'Reggae Sunday', time: 'Sunday | 9AM-12PM', desc: 'Best in Reggae and Dancehall' },
                { name: 'Late Night Mix', time: 'Fri-Sat | 10PM-2AM', desc: 'Late night energy with Afrobeats & House' },
              ].map((show, i) => (
                <Card key={i} className="bg-black border border-red-500/20 p-6 hover:border-red-500 transition-all group cursor-pointer">
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-500 transition-all">
                    <Music className="w-6 h-6 text-red-500 group-hover:text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2 group-hover:text-red-500 transition-colors">{show.name}</h3>
                  <p className="text-sm text-red-500 font-mono mb-3">{show.time}</p>
                  <p className="text-white/60">{show.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Events with Large Cards */}
        <section id="events" className="py-24 px-4 lg:px-16 bg-black">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-7xl lg:text-9xl font-black mb-16 leading-none">
              UPCOMING<br />
              <span className="text-red-500">EVENTS</span>
            </h2>

            <div className="space-y-6">
              {[
                { date: '15', month: 'JAN', name: 'Live DJ Set: C Fresh', time: 'Friday 8PM-11PM', desc: 'Unforgettable night of classic hip hop hits mixed live' },
                { date: '22', month: 'JAN', name: 'Diaspora Stories Hour', time: 'Tuesday 7PM-8PM', desc: 'Powerful stories from the diaspora community' },
                { date: '28', month: 'JAN', name: 'Reggae Sunday Special', time: 'Sunday 9AM-12PM', desc: 'Classic Bob Marley, modern dancehall and more' },
              ].map((event, i) => (
                <Card key={i} className="bg-gradient-to-r from-red-500/10 to-black border border-red-500/20 p-8 hover:border-red-500 transition-all group cursor-pointer">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                    <div className="bg-red-500 rounded-2xl p-6 text-center min-w-[120px]">
                      <div className="text-5xl font-black text-white">{event.date}</div>
                      <div className="text-sm font-bold text-white/80 uppercase tracking-widest">{event.month}</div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-3xl font-bold mb-2 group-hover:text-red-500 transition-colors">{event.name}</h3>
                      <p className="text-red-500 font-mono text-sm mb-3">{event.time}</p>
                      <p className="text-white/60 text-lg">{event.desc}</p>
                    </div>
                    <button className="px-6 py-3 bg-red-500 hover:bg-red-600 rounded-full font-bold uppercase text-sm tracking-wider transition-all">
                      Add to Calendar
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 px-4 lg:px-16 bg-gradient-to-b from-black via-red-900/10 to-black">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Contact Info */}
              <div>
                <h2 className="text-7xl font-black mb-8 leading-none">
                  GET IN<br />
                  <span className="text-red-500">TOUCH</span>
                </h2>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Email</p>
                      <p className="text-lg font-bold">info@wvodpower93.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Phone</p>
                      <p className="text-lg font-bold">+1 (555) POWER-93</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm text-white/60">Location</p>
                      <p className="text-lg font-bold">Broadcasting to the Diaspora Worldwide</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <a href="#" className="w-14 h-14 bg-red-500/20 hover:bg-red-500 rounded-lg flex items-center justify-center transition-all">
                    <Facebook className="w-6 h-6 text-white" />
                  </a>
                  <a href="#" className="w-14 h-14 bg-red-500/20 hover:bg-red-500 rounded-lg flex items-center justify-center transition-all">
                    <Instagram className="w-6 h-6 text-white" />
                  </a>
                  <a href="#" className="w-14 h-14 bg-red-500/20 hover:bg-red-500 rounded-lg flex items-center justify-center transition-all">
                    <Twitter className="w-6 h-6 text-white" />
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <Card className="bg-black border border-red-500/20 p-8">
                  <h3 className="text-3xl font-bold mb-6">Send us a message</h3>
                  <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Your Name"
                      value={feedbackForm.name}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                      className="bg-white/5 border-red-500/20 text-white placeholder:text-white/40"
                      required
                    />
                    <Input
                      type="email"
                      placeholder="Your Email"
                      value={feedbackForm.email}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                      className="bg-white/5 border-red-500/20 text-white placeholder:text-white/40"
                      required
                    />
                    <Textarea
                      placeholder="Your Message"
                      value={feedbackForm.message}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                      className="bg-white/5 border-red-500/20 text-white placeholder:text-white/40 min-h-32"
                      required
                    />
                    <Button
                      type="submit"
                      className="w-full bg-red-500 hover:bg-red-600 rounded-full py-6 font-bold uppercase tracking-wider"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 lg:px-16 bg-black border-t border-red-500/20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Radio className="w-8 h-8 text-red-500" />
              <h3 className="text-3xl font-black">WVOD POWER 93</h3>
            </div>
            <p className="text-white/60 mb-4">The Voice of the Diaspora</p>
            <p className="text-sm text-white/40">
              © 2025 WVOD Power 93. All rights reserved. | Music the way it used to be.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home2;
